import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { CircularBadge } from "@/components/site/Logo";
import { scrollToId } from "@/lib/motion";
import { track } from "@/lib/analytics";

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.85, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Hero({ hero }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const badgeY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  const headline = hero?.headline || "";
  const words = headline.split(" ");
  // split into two visual lines roughly
  const mid = Math.ceil(words.length / 2);
  const lines = [words.slice(0, mid).join(" "), words.slice(mid).join(" ")];

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center overflow-hidden bg-navy noise pt-24 pb-16"
    >
      <div className="absolute inset-0 hero-grid" />
      <motion.div style={{ y: glowY }} className="absolute inset-0 hero-glow opacity-70" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-navy to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-8">
          <motion.div
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 backdrop-blur mb-7"
          >
            <Sparkles size={14} className="text-brand-cyan" />
            <span className="font-mono text-[0.7rem] uppercase tracking-[0.2em] text-slate-300">
              {hero?.eyebrow || "Software, systems and content"}
            </span>
          </motion.div>

          <h1 className="font-display font-bold text-white tracking-tight leading-[1.02] text-[2.6rem] sm:text-6xl lg:text-[4.6rem]">
            {lines.map((ln, i) => (
              <span key={i} className="reveal-mask">
                <motion.span
                  custom={i} variants={lineReveal} initial="hidden" animate="show"
                  className={`block ${i === lines.length - 1 ? "text-gradient" : ""}`}
                >
                  {ln}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.8 }}
            className="mt-7 max-w-2xl text-lg sm:text-xl text-slate-300 leading-relaxed"
          >
            {hero?.subheadline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.8 }}
            className="mt-9 flex flex-col sm:flex-row gap-3"
          >
            <button
              data-testid="hero-primary-cta"
              onClick={() => { track("cta_click", { section: "hero" }); scrollToId("quote"); }}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue px-7 py-3.5 text-base font-semibold text-white shadow-xl shadow-brand-blue/30 hover:bg-blue-500 transition-all active:scale-[0.98]"
            >
              {hero?.primary_cta || "Request a Quote"}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => scrollToId("services")}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-white/5 px-7 py-3.5 text-base font-semibold text-white backdrop-blur hover:bg-white/10 transition-all active:scale-[0.98]"
            >
              {hero?.secondary_cta || "Start a Project"}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.8 }}
            className="mt-14 grid grid-cols-3 gap-6 max-w-lg"
          >
            {(hero?.stats || []).map((s, i) => (
              <div key={i} className="border-l border-white/15 pl-4">
                <div className="font-display text-xl sm:text-2xl font-bold text-white">{s.value}</div>
                <div className="text-xs text-slate-400 mt-1 leading-snug">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="hidden lg:flex lg:col-span-4 justify-center">
          <motion.div
            style={{ y: badgeY }}
            initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.5, duration: 1 }}
            className="relative"
          >
            <motion.div
              animate={{ rotate: 360 }} transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
              className="drop-shadow-[0_0_40px_rgba(37,99,235,0.35)]"
            >
              <CircularBadge className="w-64 xl:w-80" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
